import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Typography } from '@mui/material';
import { ApplicationQuestionAndAnswerDto, CustomQuestionTypeEnum, CustomQuestionResponseTypeEnum, ApplicationQuestionResponseV2Dto } from '@inosio/payments-sdk';
import { selectAuthState } from '../../../shared/sagas/auth/authSlice';
import { applicationStyles } from './CustomStyles';
import { useQuery } from '../../../pmui/transactions/Transactions';
import { useEffect, useRef, useState } from 'react';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { selectApplicantsState, updateCustomQuestionsAnswersV2 } from '../sagas/applicants/applicantsSlice';
import CustomQuestionsSection from './CustomQuestionsSection';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';

interface PropsType {}

/**
 * This is the dedicated custom question page.
 * @param props
 * @returns
 */
export default function ApplicationCustomQuestion(props: PropsType) {
  const query = useQuery();
  const hasValidationQueryParam = !!query.get('validation');
  const dispatch = useDispatch();
  const classes = applicationStyles();

  const { ipaUser } = useSelector(selectAuthState);
  const { propertyGroupDetails, isFetchingPropertyGroupDetailsSuccess, isFetchingPropertyGroupDetailsFailed } = useSelector(selectPropertyGroupDetailsState);
  const { CustomQuestionAnswers, isFetchingCustomQuestionAnswersSuccess, isFetchingCustomQuestionAnswersFailure, Application } = useSelector(selectApplicantsState);

  const [showLocalSpinner, setShowLocalSpinner] = useState<boolean>(true);
  const [localError, setLocalError] = useState<string | null>(null);
  const [questionsAndAnswers, setQuestionsAndAnswers] = useState<Array<ApplicationQuestionAndAnswerDto>>([]);
  const bottomRef = useRef<HTMLElement>(null);

  //as soon as the questions load, populate the UI
  useEffect(() => {
    if (propertyGroupDetails?.Uuid && isFetchingCustomQuestionAnswersSuccess) {
      const organizationPersonalInfoCustomQuestions = (propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsQuestions ?? []).filter(
        (q) => q.CustomQuestionType === CustomQuestionTypeEnum.CustomV1,
      );
      setQuestionsAndAnswers(
        organizationPersonalInfoCustomQuestions.map((q) => {
          const previouslySavedResponseToThisQuestion = CustomQuestionAnswers?.find((a) => a.QuestionUuid === q.Uuid && a.BelongsToUuid === ipaUser?.currentProfile.uuid);

          const question: ApplicationQuestionAndAnswerDto = {
            ...q,
            ApplicantUuid: ipaUser?.currentProfile.uuid!,
            BelongsToEntityUuid: ipaUser?.currentProfile.uuid!,
          };

          switch (question.CustomQuestionResponseType) {
            case CustomQuestionResponseTypeEnum.FreeText:
              question.TextQuestionAnswer = previouslySavedResponseToThisQuestion?.Response?.toString();
              break;
            case CustomQuestionResponseTypeEnum.Boolean:
              question.BooleanQuestionAnswer = previouslySavedResponseToThisQuestion?.Response === undefined ? undefined : Boolean(previouslySavedResponseToThisQuestion?.Response);
              break;
            default:
              break;
          }

          return question;
        }),
      );
    }
  }, [propertyGroupDetails, ipaUser, isFetchingCustomQuestionAnswersSuccess]);

  //error handling
  //show or hide spinner depending on what data has been loaded.
  useEffect(() => {
    if (isFetchingPropertyGroupDetailsSuccess && isFetchingCustomQuestionAnswersSuccess) {
      //life is good
      setShowLocalSpinner(false);
      setLocalError(null);
    } else if (isFetchingPropertyGroupDetailsFailed || isFetchingCustomQuestionAnswersFailure) {
      //life is bad
      setShowLocalSpinner(false);
      setLocalError('There was an error loading the additional questions. Try refreshing the page.');
    } else {
      setShowLocalSpinner(true);
    }
  }, [isFetchingPropertyGroupDetailsSuccess, isFetchingPropertyGroupDetailsFailed, isFetchingCustomQuestionAnswersSuccess]);  

  const handleSaveCustomQuestionAnswer = (e: any, questionUuid: string) => {
    const answeredQuestion = questionsAndAnswers.find((q) => q.Uuid === questionUuid);

    if (answeredQuestion) {
      dispatch({
        type: updateCustomQuestionsAnswersV2.type,
        payload: {
          applicationUuid: Application.Uuid,
          upsertDto: {
            QuestionUuid: answeredQuestion?.Uuid,
            ResponseType: answeredQuestion?.CustomQuestionResponseType,
            Response: answeredQuestion.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.Boolean ? e.target.value === 'true' : e.target.value,
            BelongsToUuid: ipaUser?.currentProfile.uuid,
            ApplicantUuid: ipaUser?.currentProfile.uuid,
          } as ApplicationQuestionResponseV2Dto,
        },
      });
    }
  };

  const handleCustomQuestionFieldChanged = (e: any, questionUuid: string) => {
    setQuestionsAndAnswers(
      questionsAndAnswers.map((qa) => {
        if (qa.Uuid === questionUuid) {
          //found our question.

          if (qa.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.Boolean) {
            return {
              ...qa,
              BooleanQuestionAnswer: e.target.value === 'true',
            };
          } else
            return {
              ...qa,
              TextQuestionAnswer: e.target.value,
            };
        }
        return qa;
      }),
    );
  };

  return (
    <Box width="100%" minHeight="60vh">
      {showLocalSpinner ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', minHeight: 'inherit', alignItems: 'center' }}>
          <SpinnerWithNoConfirmation />
        </Box>
      ) : (
        <Box sx={{ width: '100%', minHeight: 'inherit', p: 4 }}>
          {localError && (
            <Box sx={{ width: '100%', justifyContent: 'center', textAlign: 'center', m: 2 }}>
              <Typography className={classes.globalText}>{localError}</Typography>
            </Box>
          )}
          <Grid container spacing={4}>
            <CustomQuestionsSection
              questionsAndAnswers={questionsAndAnswers}
              hasValidationQueryParam={hasValidationQueryParam}
              onFieldChange={handleCustomQuestionFieldChanged}
              onFieldUpdate={handleSaveCustomQuestionAnswer}
              bottomRef={bottomRef}
              hideHeader={true}
            />
          </Grid>
        </Box>
      )}
    </Box>
  );
}
